/**
 * Review Intelligence Prompt — P3
 *
 * Compact modal shown BEFORE any manual review action commits. Captures:
 *   - reason_code (dropdown — must mirror REASON_CODES on the backend)
 *   - user_confidence (3-option pill: Low / Medium / High)
 *   - optional reason_text (textarea, ≤1000 chars)
 *
 * Used by:
 *   - Approve flow on the review queue row
 *   - Recalculate flow on the review queue row
 *
 * For Edit + Manual Allocate, the existing modals embed the same three
 * fields inline (so users don't see two modals in a row).
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Brain } from "lucide-react";

export const REVIEW_REASON_OPTIONS = [
  { value: "wrong_vault", label: "Wrong vault" },
  { value: "wrong_counterparty", label: "Wrong counterparty" },
  { value: "wrong_direction", label: "Wrong direction" },
  { value: "wrong_currency", label: "Wrong currency" },
  { value: "wrong_amount", label: "Wrong amount" },
  { value: "missing_context", label: "Missing context" },
  { value: "duplicate", label: "Duplicate transaction" },
  { value: "internal_transfer", label: "Internal transfer" },
  { value: "low_confidence", label: "Low confidence" },
  { value: "other", label: "Other" },
];

export const REVIEW_CONFIDENCE_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const ACTION_TITLES = {
  approve: "Approve Record",
  recalculate: "Recalculate Record",
  reject: "Reject Record",
};

const ACTION_DESCRIPTIONS = {
  approve:
    "Approving will lock this allocation into the ledger. Every approval " +
    "is recorded as learning data for Sentient Vault's Counterparty Memory.",
  recalculate:
    "Recalculating re-runs rules + AI on this record. Why are you " +
    "asking for a recompute?",
  reject:
    "Rejecting will mark this record as not-for-vault. Tell us why so " +
    "Sentient Vault learns from your judgement.",
};

/**
 * @param {{
 *   open: boolean,
 *   action: "approve" | "recalculate" | "reject",
 *   record: any,
 *   onClose: () => void,
 *   onConfirm: (review: { reason_code: string, user_confidence: string, reason_text: string }) => Promise<void>,
 * }} props
 */
export default function ReviewIntelligencePrompt({
  open, action, record, onClose, onConfirm,
}) {
  const [reasonCode, setReasonCode] = useState("");
  const [confidence, setConfidence] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset form when the prompt re-opens. This pattern (useEffect + setState)
  // matches the other modals in this folder (EditRecordModal etc.).
  useEffect(() => {
    if (open) {
      setReasonCode("");
      setConfidence("");
      setReasonText("");
    }
  }, [open]);

  const valid = Boolean(reasonCode) && Boolean(confidence);

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onConfirm({
        reason_code: reasonCode,
        user_confidence: confidence,
        reason_text: reasonText.trim(),
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const np = record?.normalized_payload || {};
  const title = ACTION_TITLES[action] || "Review Intelligence";
  const description = ACTION_DESCRIPTIONS[action] || "";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent
        className="bg-[#0B1120] border-slate-800 max-w-md"
        data-testid="review-intelligence-prompt"
      >
        <DialogHeader>
          <DialogTitle className="font-barlow text-slate-50 flex items-center gap-2">
            <Brain className="w-4 h-4 text-cyan-400" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs leading-relaxed">
            {description}
          </DialogDescription>
        </DialogHeader>

        {record && (
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30 mb-1">
            <div className="text-xs text-slate-300 line-clamp-1">
              {np.description || "No description"}
            </div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">
              {np.counterparty || "Unknown"} · {np.source_currency || "GBP"}{" "}
              {np.source_amount?.toLocaleString?.() || np.source_amount || "0"}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div data-testid="review-reason-field">
            <Label className="text-xs text-slate-400">
              Why are you changing this?
              <span className="ml-2 text-[10px] text-amber-400">required</span>
            </Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger
                className="bg-slate-900/50 border-slate-700 text-xs mt-1"
                data-testid="review-reason-trigger"
              >
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {REVIEW_REASON_OPTIONS.map((r) => (
                  <SelectItem
                    key={r.value} value={r.value}
                    data-testid={`review-reason-option-${r.value}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div data-testid="review-confidence-field">
            <Label className="text-xs text-slate-400">
              How confident are you?
              <span className="ml-2 text-[10px] text-amber-400">required</span>
            </Label>
            <div
              className="grid grid-cols-3 gap-2 mt-1.5"
              role="radiogroup"
              aria-label="Reviewer confidence"
            >
              {REVIEW_CONFIDENCE_OPTIONS.map((c) => {
                const active = confidence === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setConfidence(c.value)}
                    aria-checked={active}
                    role="radio"
                    data-testid={`review-confidence-${c.value}`}
                    className={[
                      "text-[11px] font-semibold py-2 rounded-md border transition-colors",
                      active
                        ? "border-cyan-400 bg-cyan-500/15 text-cyan-300"
                        : "border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600 hover:text-slate-200",
                    ].join(" ")}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div data-testid="review-note-field">
            <Label className="text-xs text-slate-400">
              Optional note
              <span className="ml-2 text-[10px] text-slate-500">
                helps Sentient Vault learn faster
              </span>
            </Label>
            <Textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value.slice(0, 1000))}
              placeholder="A short note about why this needs your judgement…"
              className="bg-slate-900/50 border-slate-700 mt-1 min-h-[60px] text-xs"
              data-testid="review-note-input"
            />
            <div className="text-[10px] text-slate-500 mt-1 text-right">
              {reasonText.length} / 1000
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="border-slate-700"
            data-testid="review-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!valid || submitting}
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="review-confirm-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving…
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
